import { cva, type VariantProps } from 'class-variance-authority';

const defaultVariantClasses =
  'flex box-border duration-100 heading-auto justify-center items-center disabled:cursor-not-allowed disabled:opacity-50';

const dropdownItemClasses =
  'group gap-2.25 min-h-11 rounded-md  px-3.5 py-2.5 text-xs font-normal leading-normal hover:bg-gradient-card hover:text-white disabled:cursor-not-allowed disabled:opacity-50';

export const buttonVariants = cva('', {
  variants: {
    variant: {
      clean: '',
      default: defaultVariantClasses,
      'primary-contained': [
        defaultVariantClasses,
        `transition-all bg-blue-500 text-white hover:opacity-90 active:opacity-80 disabled:bg-neutral-80 disabled:text-neutral-400 light:bg-blue-500`,
      ],
      'secondary-contained': [
        defaultVariantClasses,
        `transition-all border hover:opacity-90 active:opacity-70 border-[#2D3236] text-white bg-secondary-gradient light:bg-none light:bg-white light:text-neutral-800 light:border-neutral-80`,
      ],
      'secondary-gray-contained': [
        defaultVariantClasses,
        `transition-none text-white active:opacity-70 bg-secondaryGray-gradient border-[#384350] border hover:opacity-90 disabled:text-neutral-200 disabled:text-neutral-400`,
      ],
      'secondary-black-contained': [
        defaultVariantClasses,
        `transition-none text-white active:opacity-70 hover:text-white hover:bg-secondaryBlack-gradient hover:border-[#323A45] hover:border border-transparent border hover:opacity-90 light:bg-transparent disabled:text-neutral-200 disabled:text-neutral-400 light:text-black light:hover:bg-black light:bg-none light:hover:text-white light:active:opacity-80 light:border-0`,
      ],
      'black-contained': [
        defaultVariantClasses,
        `transition-all hover:opacity-90 active:opacity-80 disabled:bg-neutral-200 disabled:text-neutral-400 bg-white text-black light:bg-black light:text-white`,
      ],
      'secondary-blue-contained': [
        defaultVariantClasses,
        `transition-none text-white active:opacity-70 enabled:bg-secondaryGray-gradient border-[#384350] border hover:opacity-90 disabled:bg-charcoal-800 disabled:text-charcoal-500 disabled:border-[transparent] disabled:!opacity-100`,
      ],
      'white-contained': [
        defaultVariantClasses,
        `transition-all border-2 light:border-black hover:opacity-90 active:opacity-70 border-white bg-black text-white light:bg-white light:text-black`,
      ],
      'charcoal-contained': [
        defaultVariantClasses,
        ` bg-charcoal-500 text-neutral-50`,
      ],
      'eerieblack-contained': [
        defaultVariantClasses,
        `border border-dark-gunmetal-200 bg-eerieblack-gradient`,
      ],
      'gray-translucent-contained': [
        defaultVariantClasses,
        `bg-[rgba(248,248,248,0.08)] text-white hover:bg-[rgba(248,248,248,0.1)] transition-all active:opacity-90`,
      ],
      'black-to-white-contained': [
        defaultVariantClasses,
        `transition-none bg-transparent text-white hover:bg-black hover:text-white hover:opacity-90 active:opacity-80 disabled:text-neutral-200 disabled:text-neutral-400 light:text-black`,
      ],
      'dark-neutral-contained': [
        defaultVariantClasses,
        'bg-neutral-800 hover:opacity-90',
      ],
      'white-outlined': [
        defaultVariantClasses,
        `border border-white text-white hover:opacity-90`,
      ],
      'transition-white-outlined': [
        defaultVariantClasses,
        'transition-all border light:border-black hover:opacity-90 active:opacity-70 border-white bg-transparent text-white',
      ],
      'dropdown-item': [
        defaultVariantClasses,
        'justify-start',
        dropdownItemClasses,
      ],
      'dark-dropdown-item': [
        defaultVariantClasses,
        dropdownItemClasses,
        'justify-start bg-gradient-card hover:opacity-80',
      ],
      'dark-gunmetal-contained': [
        defaultVariantClasses,
        'justify-start bg-dark-gunmetal-gradient border border-charcoal-600 hover:opacity-80',
      ],
      'primary-dropdown-item': [
        dropdownItemClasses,
        'transition-all bg-blue-500 text-white hover:opacity-90 active:opacity-80 disabled:bg-neutral-80 disabled:text-neutral-400 light:bg-blue-500',
      ],
      'secondary-gray-dropdown-item': [
        dropdownItemClasses,
        `transition-none text-white active:opacity-70 enabled:bg-secondaryGray-gradient border-[#384350] border hover:opacity-90 disabled:bg-charcoal-800 disabled:text-charcoal-500 disabled:border-[transparent] disabled:!opacity-100`,
      ],
    },
    size: {
      '2xs': 'h-5.5 min-w-6 px-2 rounded-md text-4xs font-light gap-2',
      xs: 'h-6 min-w-6 px-2 text-xs leading-normal font-medium gap-[5px] rounded',
      sm: 'h-7.5 min-w-7.5 px-2.5 text-2sm leading-normal font-medium gap-1.5 rounded-[5px]',
      md: 'h-10 min-w-10 px-4.5 text-md font-medium gap-2.5 rounded-lg leading-4',
      lg: 'h-12 min-w-12 px-5.5 text-base font-medium gap-2.5 rounded-[10px]',
      xl: 'h-14 min-w-14 px-6.5 text-lg leading-normal font-medium gap-2.5 rounded-xl',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
